import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from './index';
import { getUnclaimedContentService } from './utils/serviceLoader';

// Types
import { 
UnclaimedContent, 
ContentSource, 
UnclaimedContentFilters, 
UnclaimedContentStats,
ClaimRequest,
EvidenceItem,
VaultMetadata,
ClaimMessage,
ContentDiscoverySession,
ClaimVerificationRule,
ClaimRequestFormData
} from '@/types/unclaimed-content';
// import unclaimedContentService from '@/services/unclaimedContentService';

export class UnclaimedContentStore {
rootStore: RootStore;
private serviceCache: any = null;

// Core Data - Unclaimed Content
contents: UnclaimedContent[] = [];
selectedContent: UnclaimedContent | null = null;
featuredContents: UnclaimedContent[] = [];

// Claims Management
myClaimRequests: ClaimRequest[] = [];
allClaimRequests: ClaimRequest[] = []; // For admin/reviewers
selectedClaimRequest: ClaimRequest | null = null;

// Evidence & Messages
evidenceItems: EvidenceItem[] = [];
claimMessages: ClaimMessage[] = [];

// Discovery & Vault
vaultMetadata: VaultMetadata[] = [];
discoverySession: ContentDiscoverySession | null = null;
verificationRules: ClaimVerificationRule[] = [];

// Filters and Search
filters: UnclaimedContentFilters = {
  sources: [],
  contentTypes: [],
  status: [],
  potentialRevenueRange: { min: 0, max: 0 },
  searchQuery: '',
  sortBy: 'potentialRevenue',
  sortOrder: 'desc'
};

// UI State
isLoading = false;
isSubmitting = false;
error: string | null = null;

// Pagination
page = 1;
pageSize = 20;
total = 0;
hasMore = true;

// Statistics
stats: UnclaimedContentStats | null = null;

// Current active source/view
activeSource: ContentSource | null = null;
viewMode: 'all' | 'vault' | 'orphan' | 'my-claims' = 'all';

constructor(rootStore: RootStore) {
  makeAutoObservable(this);
  this.rootStore = rootStore;
}

// Service accessor with caching
private async getService() {
  if (!this.serviceCache) {
    this.serviceCache = await getUnclaimedContentService();
  }
  return this.serviceCache;
}

// =====================================
// COMPUTED GETTERS
// =====================================

get hasContent(): boolean {
  return Array.isArray(this.contents) && this.contents.length > 0;
}

get totalPotentialRevenue(): number {
  if (!Array.isArray(this.contents)) return 0;
  return this.contents.reduce((sum, content) => 
    sum + (content.attributes?.potentialRevenue || 0), 0
  );
}

get activeFilterCount(): number {
  let count = 0;
  if (this.filters?.contentTypes?.length) count++;
  if (this.filters?.sources?.length) count++;
  if (this.filters?.status?.length) count++;
  if (this.filters?.potentialRevenueRange?.min || this.filters?.potentialRevenueRange?.max) count++;
  if (this.filters?.searchQuery?.trim()) count++;
  return count;
}

get contentBySource(): Record<ContentSource, UnclaimedContent[]> {
  if (!Array.isArray(this.contents)) return {} as Record<ContentSource, UnclaimedContent[]>;
  
  return this.contents.reduce((acc, content) => {
    const source = content.attributes?.source;
    if (source) {
      if (!acc[source]) acc[source] = [];
      acc[source].push(content);
    }
    return acc;
  }, {} as Record<ContentSource, UnclaimedContent[]>);
}

get vaultContent(): UnclaimedContent[] {
  if (!Array.isArray(this.contents)) return [];
  return this.contents.filter(c => c.attributes?.source === 'vietrro_vault');
}

get orphanContent(): UnclaimedContent[] {
  if (!Array.isArray(this.contents)) return [];
  return this.contents.filter(c => c.attributes?.source === 'orphan_internet');
}

get availableContent(): UnclaimedContent[] {
  if (!Array.isArray(this.contents)) return [];
  return this.contents.filter(c => c.attributes?.contentStatus === 'available');
}

get claimedContent(): UnclaimedContent[] {
  if (!Array.isArray(this.contents)) return [];
  return this.contents.filter(c => c.attributes?.contentStatus === 'claimed');
}

get pendingClaims(): ClaimRequest[] {
  if (!Array.isArray(this.myClaimRequests)) return [];
  return this.myClaimRequests.filter(c => c.attributes?.claimStatus === 'pending');
}

get approvedClaims(): ClaimRequest[] {
  if (!Array.isArray(this.myClaimRequests)) return [];
  return this.myClaimRequests.filter(c => c.attributes?.claimStatus === 'approved');
}

get rejectedClaims(): ClaimRequest[] {
  if (!Array.isArray(this.myClaimRequests)) return [];
  return this.myClaimRequests.filter(c => c.attributes?.claimStatus === 'rejected');
}

get highValueContent(): UnclaimedContent[] {
  if (!Array.isArray(this.contents)) return [];
  return this.contents.filter(c => (c.attributes?.potentialRevenue || 0) >= 1000000);
}

// =====================================
// INITIALIZATION & DATA LOADING
// =====================================

async initialize(source?: ContentSource, viewMode: typeof this.viewMode = 'all'): Promise<void> {
  runInAction(() => {
    this.isLoading = true;
    this.error = null;
    this.activeSource = source || null;
    this.viewMode = viewMode;
    
    // Reset filters for new source
    if (source) {
      this.filters = {
        ...this.filters,
        sources: [source]
      };
    }
  });

  try {
    const promises = [
      this.loadUnclaimedContents(),
      this.loadStats(),
    ];

    // Load user-specific data if authenticated
    if (this.rootStore.authStore.currentUser) {
      promises.push(this.loadMyClaimRequests());
      
      // Load admin data if user has permissions
      if (this.canReviewClaims()) {
        promises.push(this.loadPendingClaimRequests());
      }
    }

    await Promise.all(promises);

  } catch (error) {
    runInAction(() => {
      this.error = this.getErrorMessage(error);
      this.isLoading = false;
    });
  }
}

async loadUnclaimedContents(reset = true): Promise<void> {
  if (reset) {
    runInAction(() => {
      this.isLoading = true;
      this.error = null;
      this.page = 1;
    });
  }

  try {
    const service = await this.getService();
    const response = await service.getUnclaimedContents({
      page: reset ? 1 : this.page + 1,
      pageSize: this.pageSize,
      filters: this.filters,
      populate: ['claim_requests.claimant', 'vault_metadata', 'discoveredBy'],
      sort: [`${this.filters.sortBy}:${this.filters.sortOrder}`]
    });

    runInAction(() => {
      if (reset) {
        this.contents = Array.isArray(response.data) ? response.data : [];
      } else {
        const newData = Array.isArray(response.data) ? response.data : [];
        this.contents = [...(Array.isArray(this.contents) ? this.contents : []), ...newData];
      }
      
      this.total = response.meta?.pagination?.total || 0;
      this.page = response.meta?.pagination?.page || 1;
      this.hasMore = (response.meta?.pagination?.page || 1) < (response.meta?.pagination?.pageCount || 1);
      this.isLoading = false;
    });

  } catch (error) {
    runInAction(() => {
      this.error = this.getErrorMessage(error);
      this.isLoading = false;
      // Initialize contents as empty array if it's undefined
      if (!Array.isArray(this.contents)) {
        this.contents = [];
      }
    });
  }
}

async loadMoreContent(): Promise<void> {
  if (!this.hasMore || this.isLoading) return;
  await this.loadUnclaimedContents(false);
}

async loadStats(): Promise<void> {
  try {
    const service = await this.getService();
    const response = await service.getStats(
      this.activeSource ? { sources: [this.activeSource] } : {}
    );
    
    runInAction(() => {
      this.stats = response.data;
    });
  } catch (error) {
    console.error('Failed to load stats:', error);
  }
}

// =====================================
// SEARCH & FILTERING
// =====================================

// Remove duplicate

// async searchContent(query: string, additionalFilters?: Partial<UnclaimedContentFilters>): Promise<void> {
//   const newFilters = {
//     ...this.filters,
//     searchQuery: query,
//     ...additionalFilters
//   };

//   await this.applyFilters(newFilters);
// }

// async updateFilters(newFilters: Partial<UnclaimedContentFilters>): Promise<void> {
//   const updatedFilters = { ...this.filters, ...newFilters };
//   await this.applyFilters(updatedFilters);
// }

// private async applyFilters(newFilters: UnclaimedContentFilters): Promise<void> {
//   runInAction(() => {
//     this.filters = newFilters;
//   });

//   await this.loadUnclaimedContents(true);
// }

// async clearFilters(): Promise<void> {
//   const clearedFilters: UnclaimedContentFilters = {
//     sources: this.activeSource ? [this.activeSource] : [],
//     contentTypes: [],
//     status: [],
//     potentialRevenueRange: { min: 0, max: 0 },
//     searchQuery: '',
//     sortBy: 'potentialRevenue',
//     sortOrder: 'desc'
//   };

//   await this.applyFilters(clearedFilters);
// }

// async advancedSearch(searchParams: {
//   query: string;
//   sources?: ContentSource[];
//   contentTypes?: string[];
//   valueRange?: { min: number; max: number };
//   dateRange?: { from: string; to: string };
//   hasClaims?: boolean;
//   claimStatus?: string[];
// }): Promise<void> {
//   runInAction(() => {
//     this.isLoading = true;
//   });

//   try {
//     const service = await this.getService();
//     const response = await service.advancedSearch({
//       ...searchParams,
//       page: 1,
//       pageSize: this.pageSize
//     });

//     runInAction(() => {
//       this.contents = Array.isArray(response.data) ? response.data : [];
//       this.total = response.meta?.pagination?.total || 0;
//       this.hasMore = false; // Advanced search doesn't support pagination yet
//       this.isLoading = false;
//     });

//   } catch (error) {
//     runInAction(() => {
//       this.error = this.getErrorMessage(error);
//       this.isLoading = false;
//       if (!Array.isArray(this.contents)) {
//         this.contents = [];
//       }
//     });
//   }
// }

// // =====================================
// // CLAIM REQUEST MANAGEMENT
// // =====================================

// // async submitClaimRequest(contentId: string, claimData: ClaimRequestFormData): Promise<void> {
// //   runInAction(() => {
// //     this.isSubmitting = true;
// //     this.error = null;
// //   });

// //   try {
// //     const service = await this.getService();
// //     const response = await service.submitClaimRequest(contentId, claimData);

// //     runInAction(() => {
// //       // Update the content's claim requests
// //       const contentIndex = this.contents.findIndex(c => c.documentId === contentId);
// //       if (contentIndex >= 0) {
// //         const content = this.contents[contentIndex];
// //         if (!content.claim_requests) {
// //           content.claim_requests = [];
// //         }
// //         content.claim_requests.push(response.data);
// //       }
      
// //       // Add to user's claim requests
// //       this.myClaimRequests = [response.data, ...this.myClaimRequests];
// //       this.isSubmitting = false;
// //     });

// //     // Show success notification
// //     this.showSuccessMessage('Yêu cầu claim đã được gửi thành công!');

// //   } catch (error) {
// //     runInAction(() => {
// //       this.error = this.getErrorMessage(error);
// //       this.isSubmitting = false;
// //     });

// //     this.showErrorMessage(this.getErrorMessage(error));
// //   }
// // }

// // async loadMyClaimRequests(): Promise<void> {
// //   if (!this.rootStore.authStore.currentUser) return;

// //   try {
// //     const service = await this.getService();
// //     const response = await service.getUserClaimRequests(
// //       this.rootStore.authStore.currentUser.id,
// //       { pageSize: 100 }
// //     );

// //     runInAction(() => {
// //       this.myClaimRequests = Array.isArray(response.data) ? response.data : [];
// //     });

// //   } catch (error) {
// //     console.error('Failed to load claim requests:', error);
// //   }
// // }

// // async loadPendingClaimRequests(): Promise<void> {
// //   try {
// //     const service = await this.getService();
// //     const response = await service.getPendingClaimRequests({
// //       pageSize: 50
// //     });

// //     runInAction(() => {
// //       this.allClaimRequests = Array.isArray(response.data) ? response.data : [];
// //     });

// //   } catch (error) {
// //     console.error('Failed to load pending claims:', error);
// //   }
// // }

// // async updateClaimRequest(requestId: string, updateData: any): Promise<void> {
// //   runInAction(() => {
// //     this.isSubmitting = true;
// //     this.error = null;
// //   });

// //   try {
// //     const service = await this.getService();
// //     const response = await service.updateClaimRequest(requestId, updateData);

// //     runInAction(() => {
// //       this.updateClaimRequestInLists(response.data);
// //       this.isSubmitting = false;
// //     });

// //   } catch (error) {
// //     runInAction(() => {
// //       this.error = this.getErrorMessage(error);
// //       this.isSubmitting = false;
// //     });
// //   }
// // }

// async approveClaimRequest(requestId: string, reviewNotes?: string): Promise<void> {
//   runInAction(() => {
//     this.isSubmitting = true;
//     this.error = null;
//   });

//   try {
//     const service = await this.getService();
//     const response = await service.approveClaimRequest(requestId, reviewNotes);

//     runInAction(() => {
//       this.updateClaimRequestInLists(response.data);
      
//       // Update content status to claimed
//       const content = this.findContentByClaimRequest(requestId);
//       if (content) {
//         content.contentStatus = 'claimed';
//       }
      
//       this.isSubmitting = false;
//     });

//     this.showSuccessMessage('Claim request approved successfully!');

//   } catch (error) {
//     runInAction(() => {
//       this.error = this.getErrorMessage(error);
//       this.isSubmitting = false;
//     });
//   }
// }

// async rejectClaimRequest(requestId: string, rejectionReason: string): Promise<void> {
//   runInAction(() => {
//     this.isSubmitting = true;
//     this.error = null;
//   });

//   try {
//     const service = await this.getService();
//     const response = await service.rejectClaimRequest(requestId, rejectionReason);

//     runInAction(() => {
//       this.updateClaimRequestInLists(response.data);
//       this.isSubmitting = false;
//     });

//     this.showSuccessMessage('Claim request rejected.');

//   } catch (error) {
//     runInAction(() => {
//       this.error = this.getErrorMessage(error);
//       this.isSubmitting = false;
//     });
//   }
// }

// async withdrawClaimRequest(requestId: string): Promise<void> {
//   runInAction(() => {
//     this.isSubmitting = true;
//     this.error = null;
//   });

//   try {
//     const service = await this.getService();
//     await service.withdrawClaimRequest(requestId);

//     runInAction(() => {
//       // Remove from myClaimRequests
//       this.myClaimRequests = this.myClaimRequests.filter(r => r.documentId !== requestId);

//       // Update content's claim requests
//       this.contents.forEach(content => {
//         if (content.claim_requests) {
//           content.claim_requests = content.claim_requests.filter(
//             c => c.documentId !== requestId
//           );
//         }
//       });

//       this.isSubmitting = false;
//     });

//     this.showSuccessMessage('Claim request withdrawn successfully!');

//   } catch (error) {
//     runInAction(() => {
//       this.error = this.getErrorMessage(error);
//       this.isSubmitting = false;
//     });
//   }
// }

// =====================================
// EVIDENCE MANAGEMENT
// =====================================

// Remove duplicate

// async addEvidenceItem(claimRequestId: string, evidenceData: {
//   type: string;
//   title: string;
//   description: string;
//   file?: File;
// }): Promise<void> {
//   runInAction(() => {
//     this.isSubmitting = true;
//     this.error = null;
//   });

//   try {
//     const service = await this.getService();
//     const response = await service.createEvidenceItem(claimRequestId, evidenceData);

//     runInAction(() => {
//       // Update claim request with new evidence
//       const claimRequest = this.myClaimRequests.find(r => r.documentId === claimRequestId);
//       if (claimRequest && claimRequest.evidence_items) {
//         claimRequest.evidence_items.push(response.data);
//       }
      
//       this.isSubmitting = false;
//     });

//     this.showSuccessMessage('Evidence uploaded successfully!');

//   } catch (error) {
//     runInAction(() => {
//       this.error = this.getErrorMessage(error);
//       this.isSubmitting = false;
//     });
//   }
// }

// async verifyEvidence(evidenceId: string, status: 'verified' | 'rejected', notes?: string): Promise<void> {
//   try {
//     const service = await this.getService();
//     const response = await service.verifyEvidence(evidenceId, status, notes);

//     runInAction(() => {
//       // Update evidence in relevant claim requests
//       this.allClaimRequests.forEach(claimRequest => {
//         if (claimRequest.evidence_items) {
//           const evidenceIndex = claimRequest.evidence_items.findIndex(
//             e => e.documentId === evidenceId
//           );
//           if (evidenceIndex >= 0) {
//             claimRequest.evidence_items[evidenceIndex] = response.data;
//           }
//         }
//       });
//     });

//   } catch (error) {
//     this.showErrorMessage(this.getErrorMessage(error));
//   }
// }

// =====================================
// MESSAGING SYSTEM
// =====================================

// Remove duplicate

// async sendClaimMessage(claimRequestId: string, messageData: {
//   messageType: string;
//   subject?: string;
//   content: string;
//   isInternal?: boolean;
//   priority?: string;
//   requiresResponse?: boolean;
//   responseDeadline?: string;
//   attachments?: File[];
// }): Promise<void> {
//   try {
//     const service = await this.getService();
//     const response = await service.sendClaimMessage(claimRequestId, messageData);

//     runInAction(() => {
//       this.claimMessages = [response.data, ...this.claimMessages];
//     });

//   } catch (error) {
//     this.showErrorMessage(this.getErrorMessage(error));
//   }
// }

// async loadClaimMessages(claimRequestId: string): Promise<void> {
//   try {
//     const service = await this.getService();
//     const response = await service.getClaimMessages(claimRequestId);

//     runInAction(() => {
//       this.claimMessages = Array.isArray(response.data) ? response.data : [];
//     });

//   } catch (error) {
//     console.error('Failed to load claim messages:', error);
//   }
// }

// =====================================
// DISCOVERY & VAULT OPERATIONS
// =====================================

// Remove duplicate

// async switchSource(newSource: ContentSource): Promise<void> {
//   if (this.activeSource === newSource) return;

//   await this.initialize(newSource);
// }

// async startDiscoverySession(config: {
//   discoveryMethod: 'web_crawl' | 'ai_scan' | 'user_report' | 'bulk_import';
//   configuration: any;
// }): Promise<void> {
//   try {
//     const service = await this.getService();
//     const response = await service.startDiscoverySession(config);

//     runInAction(() => {
//       this.discoverySession = response.data;
//     });

//   } catch (error) {
//     this.showErrorMessage(this.getErrorMessage(error));
//   }
// }

// async bulkUpdateStatus(contentIds: string[], status: string): Promise<void> {
//   if (!this.activeSource || this.activeSource !== 'vietrro_vault') {
//     throw new Error('Bulk operations only available for vault content');
//   }

//   runInAction(() => {
//     this.isSubmitting = true;
//     this.error = null;
//   });

//   try {
//     const service = await this.getService();
//     await service.bulkUpdateContentStatus(contentIds, status);

//     runInAction(() => {
//       // Update local content status
//       this.contents.forEach(content => {
//         if (contentIds.includes(content.documentId)) {
//           content.contentStatus = status as any;
//         }
//       });
//       this.isSubmitting = false;
//     });

//     this.showSuccessMessage(`Updated ${contentIds.length} items successfully!`);

//   } catch (error) {
//     runInAction(() => {
//       this.error = this.getErrorMessage(error);
//       this.isSubmitting = false;
//     });
//   }
// }

// =====================================
// ANALYTICS & REPORTING
// =====================================

// Remove duplicate

// async generateAnalyticsReport(reportType: string, params: {
//   dateRange: { from: string; to: string };
//   source?: ContentSource;
//   includeDetails?: boolean;
// }): Promise<any> {
//   try {
//     const service = await this.getService();
//     const response = await service.generateAnalyticsReport(reportType, params);
//     return response.data;
//   } catch (error) {
//     this.showErrorMessage(this.getErrorMessage(error));
//     return null;
//   }
// }

// async exportData(format: 'csv' | 'xlsx' | 'json' = 'csv'): Promise<Blob | null> {
//   try {
//     const service = await this.getService();
//     return await service.exportData(this.filters, format);
//   } catch (error) {
//     this.showErrorMessage(this.getErrorMessage(error));
//     return null;
//   }
// }

// =====================================
// SEARCH & FILTERING
// =====================================

async searchContent(query: string, additionalFilters?: Partial<UnclaimedContentFilters>): Promise<void> {
  const newFilters = {
    ...this.filters,
    searchQuery: query,
    ...additionalFilters
  };

  await this.applyFilters(newFilters);
}

async updateFilters(newFilters: Partial<UnclaimedContentFilters>): Promise<void> {
  const updatedFilters = { ...this.filters, ...newFilters };
  await this.applyFilters(updatedFilters);
}

private async applyFilters(newFilters: UnclaimedContentFilters): Promise<void> {
  runInAction(() => {
    this.filters = newFilters;
  });

  await this.loadUnclaimedContents(true);
}

async clearFilters(): Promise<void> {
  const clearedFilters: UnclaimedContentFilters = {
    sources: this.activeSource ? [this.activeSource] : [],
    contentTypes: [],
    status: [],
    potentialRevenueRange: { min: 0, max: 0 },
    searchQuery: '',
    sortBy: 'potentialRevenue',
    sortOrder: 'desc'
  };

  await this.applyFilters(clearedFilters);
}

async advancedSearch(searchParams: {
  query: string;
  sources?: ContentSource[];
  contentTypes?: string[];
  valueRange?: { min: number; max: number };
  dateRange?: { from: string; to: string };
  hasClaims?: boolean;
  claimStatus?: string[];
}): Promise<void> {
  runInAction(() => {
    this.isLoading = true;
  });

  try {
    const service = await this.getService();
    const response = await service.advancedSearch({
      ...searchParams,
      page: 1,
      pageSize: this.pageSize
    });

    runInAction(() => {
      this.contents = response.data;
      this.total = response.meta?.pagination?.total || 0;
      this.hasMore = false; // Advanced search doesn't support pagination yet
      this.isLoading = false;
    });

  } catch (error) {
    runInAction(() => {
      this.error = this.getErrorMessage(error);
      this.isLoading = false;
    });
  }
}

// =====================================
// CLAIM REQUEST MANAGEMENT
// =====================================

async submitClaimRequest(contentId: string, claimData: {
  claimantInfo: {
    name: string;
    email: string;
    phone?: string;
    relationship: 'original_author' | 'inheritance' | 'license_holder' | 'account_recovery';
  };
  evidence: Array<{ 
    type: 'document' | 'certificate' | 'publication' | 'witness' | 'account_proof' | 'creation_proof' | 'other';
    title: string;
    description: string;
    file?: File;
  }>;
  statement: string;
  claimType?: string;
}): Promise<void> {
  runInAction(() => {
    this.isSubmitting = true;
    this.error = null;
  });

  try {
    const service = await this.getService();
    const response = await service.submitClaimRequest(contentId, claimData);

    runInAction(() => {
      // Update the content's claim requests
      const contentIndex = this.contents.findIndex(c => c.documentId === contentId);
      if (contentIndex >= 0) {
        const content = this.contents[contentIndex];
        if (!content.claim_requests) {
          content.claim_requests = [];
        }
        content.claim_requests.push(response.data);
      }
      
      // Add to user's claim requests
      this.myClaimRequests = [response.data, ...this.myClaimRequests];
      this.isSubmitting = false;
    });

    // Show success notification
    this.showSuccessMessage('Yêu cầu claim đã được gửi thành công!');

  } catch (error) {
    runInAction(() => {
      this.error = this.getErrorMessage(error);
      this.isSubmitting = false;
    });

    this.showErrorMessage(this.getErrorMessage(error));
  }
}

async loadMyClaimRequests(): Promise<void> {
  if (!this.rootStore.authStore.currentUser) return;

  try {
    const service = await this.getService();
    const response = await service.getUserClaimRequests(
      this.rootStore.authStore.currentUser.id,
      { pageSize: 100 } // Load all user claims
    );

    runInAction(() => {
      this.myClaimRequests = Array.isArray(response.data) ? response.data : [];
    });

  } catch (error) {
    console.error('Failed to load claim requests:', error);
  }
}

async loadPendingClaimRequests(): Promise<void> {
  try {
    const service = await this.getService();
    const response = await service.getPendingClaimRequests({
      pageSize: 50
    });

    runInAction(() => {
      this.allClaimRequests = Array.isArray(response.data) ? response.data : [];
    });

  } catch (error) {
    console.error('Failed to load pending claims:', error);
  }
}

async updateClaimRequest(requestId: string, updateData: any): Promise<void> {
  runInAction(() => {
    this.isSubmitting = true;
    this.error = null;
  });

  try {
    const service = await this.getService();
    const response = await service.updateClaimRequest(requestId, updateData);
    
    runInAction(() => {
      this.updateClaimRequestInLists(response.data);
      this.isSubmitting = false;
    });

  } catch (error) {
    runInAction(() => {
      this.error = this.getErrorMessage(error);
      this.isSubmitting = false;
    });
  }
}

async approveClaimRequest(requestId: string, reviewNotes?: string): Promise<void> {
  runInAction(() => {
    this.isSubmitting = true;
    this.error = null;
  });

  try {
    const service = await this.getService();
    const response = await service.approveClaimRequest(requestId, reviewNotes);

    runInAction(() => {
      this.updateClaimRequestInLists(response.data);
      
      // Update content status to claimed
      const content = this.findContentByClaimRequest(requestId);
      if (content) {
        content.contentStatus = 'claimed';
      }
      
      this.isSubmitting = false;
    });

    this.showSuccessMessage('Claim request approved successfully!');

  } catch (error) {
    runInAction(() => {
      this.error = this.getErrorMessage(error);
      this.isSubmitting = false;
    });
  }
}

async rejectClaimRequest(requestId: string, rejectionReason: string): Promise<void> {
  runInAction(() => {
    this.isSubmitting = true;
    this.error = null;
  });

  try {
    const service = await this.getService();
    const response = await service.updateClaimRequest(requestId, rejectionReason);
    
    runInAction(() => {
      this.updateClaimRequestInLists(response.data);
      this.isSubmitting = false;
    });

    this.showSuccessMessage('Claim request rejected.');

  } catch (error) {
    runInAction(() => {
      this.error = this.getErrorMessage(error);
      this.isSubmitting = false;
    });
  }
}

async withdrawClaimRequest(requestId: string): Promise<void> {
  runInAction(() => {
    this.isSubmitting = true;
    this.error = null;
  });

  try {
    const service = await this.getService();
    await service.withdrawClaimRequest(requestId);

    runInAction(() => {
      // Remove from myClaimRequests
      this.myClaimRequests = this.myClaimRequests.filter(r => r.documentId !== requestId);

      // Update content's claim requests
      this.contents.forEach(content => {
        if (content.claim_requests) {
          content.claim_requests = content.claim_requests.filter(
            c => c.documentId !== requestId
          );
        }
      });

      this.isSubmitting = false;
    });

    this.showSuccessMessage('Claim request withdrawn successfully!');

  } catch (error) {
    runInAction(() => {
      this.error = this.getErrorMessage(error);
      this.isSubmitting = false;
    });
  }
}

// =====================================
// EVIDENCE MANAGEMENT
// =====================================

async addEvidenceItem(claimRequestId: string, evidenceData: {
  type: string;
  title: string;
  description: string;
  file?: File;
}): Promise<void> {
  runInAction(() => {
    this.isSubmitting = true;
    this.error = null;
  });

  try {
    const service = await this.getService();
    const response = await service.createEvidenceItem(claimRequestId, evidenceData);

    runInAction(() => {
      // Update claim request with new evidence
      const claimRequest = this.myClaimRequests.find(r => r.documentId === claimRequestId);
      if (claimRequest && claimRequest.evidence_items) {
        claimRequest.evidence_items.push(response.data);
      }
      
      this.isSubmitting = false;
    });

    this.showSuccessMessage('Evidence uploaded successfully!');

  } catch (error) {
    runInAction(() => {
      this.error = this.getErrorMessage(error);
      this.isSubmitting = false;
    });
  }
}

async verifyEvidence(evidenceId: string, status: 'verified' | 'rejected', notes?: string): Promise<void> {
  try {
    const service = await this.getService();
    const response = await service.verifyEvidence(evidenceId, status, notes);

    runInAction(() => {
      // Update evidence in relevant claim requests
      this.allClaimRequests.forEach(claimRequest => {
        if (claimRequest.evidence_items) {
          const evidenceIndex = claimRequest.evidence_items.findIndex(
            e => e.documentId === evidenceId
          );
          if (evidenceIndex >= 0) {
            claimRequest.evidence_items[evidenceIndex] = response.data;
          }
        }
      });
    });

  } catch (error) {
    this.showErrorMessage(this.getErrorMessage(error));
  }
}

// =====================================
// MESSAGING SYSTEM
// =====================================

async sendClaimMessage(claimRequestId: string, messageData: {
  messageType: string;
  subject?: string;
  content: string;
  isInternal?: boolean;
  priority?: string;
  requiresResponse?: boolean;
  responseDeadline?: string;
  attachments?: File[];
}): Promise<void> {
  try {
    const service = await this.getService();
    const response = await service.sendClaimMessage(claimRequestId, messageData);

    runInAction(() => {
      this.claimMessages = [response.data, ...this.claimMessages];
    });

  } catch (error) {
    this.showErrorMessage(this.getErrorMessage(error));
  }
}

async loadClaimMessages(claimRequestId: string): Promise<void> {
  try {
    const service = await this.getService();
    const response = await service.getClaimMessages(claimRequestId);

    runInAction(() => {
      this.claimMessages = response.data;
    });

  } catch (error) {
    console.error('Failed to load claim messages:', error);
  }
}

// =====================================
// DISCOVERY & VAULT OPERATIONS
// =====================================

async switchSource(newSource: ContentSource): Promise<void> {
  if (this.activeSource === newSource) return;

  await this.initialize(newSource);
}

async startDiscoverySession(config: {
  discoveryMethod: 'web_crawl' | 'ai_scan' | 'user_report' | 'bulk_import';
  configuration: any;
}): Promise<void> {
  try {
    const service = await this.getService();
    const response = await service.startDiscoverySession(config);

    runInAction(() => {
      this.discoverySession = response.data;
    });

  } catch (error) {
    this.showErrorMessage(this.getErrorMessage(error));
  }
}

async bulkUpdateStatus(contentIds: string[], status: string): Promise<void> {
  if (!this.activeSource || this.activeSource !== 'vietrro_vault') {
    throw new Error('Bulk operations only available for vault content');
  }

  runInAction(() => {
    this.isSubmitting = true;
    this.error = null;
  });

  try {
    const service = await this.getService();
    await service.bulkUpdateContentStatus(contentIds, status);

    runInAction(() => {
      // Update local content status
      this.contents.forEach(content => {
        if (contentIds.includes(content.documentId)) {
          content.contentStatus = status as any;
        }
      });
      this.isSubmitting = false;
    });

    this.showSuccessMessage(`Updated ${contentIds.length} items successfully!`);

  } catch (error) {
    runInAction(() => {
      this.error = this.getErrorMessage(error);
      this.isSubmitting = false;
    });
  }
}

// =====================================
// ANALYTICS & REPORTING
// =====================================

async generateAnalyticsReport(reportType: string, params: {
  dateRange: { from: string; to: string };
  source?: ContentSource;
  includeDetails?: boolean;
}): Promise<any> {
  try {
    const service = await this.getService();
    const response = await service.generateAnalyticsReport(reportType, params);
    return response.data;
  } catch (error) {
    this.showErrorMessage(this.getErrorMessage(error));
    return null;
  }
}

async exportData(format: 'csv' | 'xlsx' | 'json' = 'csv'): Promise<Blob | null> {
  try {
    const service = await this.getService();
    return await service.exportData(this.filters, format);
  } catch (error) {
    this.showErrorMessage(this.getErrorMessage(error));
    return null;
  }
}

// =====================================
// HELPER METHODS & UTILITIES
// =====================================

private updateClaimRequestInLists(updatedRequest: ClaimRequest): void {
  // Update in myClaimRequests
  const myIndex = this.myClaimRequests.findIndex(r => r.documentId === updatedRequest.documentId);
  if (myIndex !== -1) {
    this.myClaimRequests[myIndex] = updatedRequest;
  }
  
  // Update in allClaimRequests
  const allIndex = this.allClaimRequests.findIndex(r => r.documentId === updatedRequest.documentId);
  if (allIndex !== -1) {
    this.allClaimRequests[allIndex] = updatedRequest;
  }
}

private findContentByClaimRequest(requestId: string): UnclaimedContent | undefined {
  return this.contents.find(content => 
    content.claim_requests?.some(cr => cr.documentId === requestId)
  );
}

private getErrorMessage(error: any): string {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.message) {
    return error.message;
  }
  return 'Có lỗi không xác định xảy ra';
}

private showSuccessMessage(message: string): void {
  // Integration with notification system
  this.rootStore.notificationStore?.showSuccess?.(message);
}

private showErrorMessage(message: string): void {
  // Integration with notification system  
  this.rootStore.notificationStore?.showError?.(message);
}

private canReviewClaims(): boolean {
  const currentUser = this.rootStore.authStore.currentUser;
  if (!currentUser) return false;
  
  const reviewRoles = ['admin', 'staff', 'legal', 'support_specialist'];
  return currentUser.roles?.some(role => reviewRoles.includes(role.code)) || false;
}

// =====================================
// CONTENT SELECTION & NAVIGATION
// =====================================

setSelectedContent(content: UnclaimedContent | null): void {
  runInAction(() => {
    this.selectedContent = content;
  });
}

setSelectedClaimRequest(claimRequest: ClaimRequest | null): void {
  runInAction(() => {
    this.selectedClaimRequest = claimRequest;
  });
}

getContentById(id: string): UnclaimedContent | undefined {
  return this.contents.find(c => c.documentId === id);
}

getClaimRequestById(id: string): ClaimRequest | undefined {
  return [...this.myClaimRequests, ...this.allClaimRequests]
    .find(r => r.documentId === id);
}

hasPendingClaim(contentId: string): boolean {
  return this.myClaimRequests.some(request => 
    request.claimStatus === 'pending' && 
    request.unclaimed_content?.documentId === contentId
  );
}

getClaimForContent(contentId: string): ClaimRequest | undefined {
  return this.myClaimRequests.find(request => 
    request.unclaimed_content?.documentId === contentId
  );
}

// =====================================
// ERROR HANDLING & STATE MANAGEMENT
// =====================================

setLoading(loading: boolean): void {
  runInAction(() => {
    this.isLoading = loading;
  });
}

setSubmitting(submitting: boolean): void {
  runInAction(() => {
    this.isSubmitting = submitting;
  });
}

setError(error: string | null): void {
  runInAction(() => {
    this.error = error;
  });
}

clearError(): void {
  runInAction(() => {
    this.error = null;
  });
}

// =====================================
// RESET & CLEANUP
// =====================================

reset(): void {
  runInAction(() => {
    this.contents = [];
    this.selectedContent = null;
    this.featuredContents = [];
    this.myClaimRequests = [];
    this.allClaimRequests = [];
    this.selectedClaimRequest = null;
    this.evidenceItems = [];
    this.claimMessages = [];
    this.vaultMetadata = [];
    this.discoverySession = null;
    this.verificationRules = [];
    
    this.filters = {
      sources: [],
      contentTypes: [],
      status: [],
      potentialRevenueRange: { min: 0, max: 0 },
      searchQuery: '',
      sortBy: 'potentialRevenue',
      sortOrder: 'desc'
    };
    
    this.isLoading = false;
    this.isSubmitting = false;
    this.error = null;
    this.page = 1;
    this.total = 0;
    this.hasMore = true;
    this.stats = null;
    this.activeSource = null;
    this.viewMode = 'all';
    
    // Reset service cache
    this.serviceCache = null;
  });
}
}

export default UnclaimedContentStore;